import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const platform: 'web' | 'android' | 'ios' = isNative ? (Capacitor.getPlatform() === 'ios' ? 'ios' : 'android') : 'web';
export const APP_NAME: string = import.meta.env.VITE_APP_NAME ?? 'Meet Dost';

/**
 * Where the API lives. On the web it is the page's own origin. A native app
 * is served from a local origin, so its build names the server explicitly.
 */
export const API_BASE: string = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

/** The public origin used in meeting links people share. */
export const PUBLIC_ORIGIN: string = API_BASE || window.location.origin;

export function wsUrl(query: Record<string, string>): string {
  const base = new URL(API_BASE || window.location.origin);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = '/ws';
  base.search = new URLSearchParams(query).toString();
  return base.toString();
}

export const isPhone = (): boolean => window.matchMedia('(max-width: 720px)').matches;

/** Copies text; returns false if the browser refused. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older WebViews and pages without clipboard permission.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  }
}

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'failed';

/** Native share sheet in the apps, Web Share where available, otherwise copy (E2-S2). */
export async function shareLink(link: string): Promise<ShareResult> {
  const text = `Join my meeting: ${link}`;
  if (isNative) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: 'Join my meeting', text, url: link, dialogTitle: 'Share meeting link' });
      return 'shared';
    } catch {
      return 'cancelled';
    }
  }
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'Join my meeting', text: 'Join my meeting:', url: link });
      return 'shared';
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') return 'cancelled';
    }
  }
  return (await copyText(link)) ? 'copied' : 'failed';
}

/**
 * Saves a small text file (a chat, a calendar entry). Browsers download it;
 * the native apps have no downloads folder of their own, so the file is
 * handed to the system share sheet, which offers Files, Calendar, mail and
 * so on.
 */
export async function saveTextFile(name: string, mime: string, content: string): Promise<boolean> {
  if (isNative) {
    try {
      const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([import('@capacitor/filesystem'), import('@capacitor/share')]);
      const { uri } = await Filesystem.writeFile({ path: name, data: content, directory: Directory.Cache, encoding: Encoding.UTF8 });
      await Share.share({ title: name, files: [uri], dialogTitle: `Save ${name}` });
      return true;
    } catch {
      // Closing the share sheet without choosing anything is not a failure worth reporting.
      return false;
    }
  }
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

/** Opens a link outside the app: a new tab on the web, the system browser from the native apps. */
export function openExternal(url: string): void {
  if (isNative) window.open(url, '_blank');
  else window.location.assign(url);
}

/** Opens this app's page in the system settings, where a blocked permission can be changed (E3-S2). */
export async function openAppSettings(): Promise<boolean> {
  if (!isNative) return false;
  try {
    const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
    const result = await NativeSettings.open({ optionAndroid: AndroidSettings.ApplicationDetails, optionIOS: IOSSettings.App });
    return result.status;
  } catch {
    return false;
  }
}

/** Keeps the screen on during a call in the native apps and, where supported, on the web (E8-S7). */
export async function setScreenAwake(awake: boolean): Promise<void> {
  try {
    if (isNative) {
      const { KeepAwake } = await import('@capacitor-community/keep-awake');
      await (awake ? KeepAwake.keepAwake() : KeepAwake.allowSleep());
      return;
    }
    if (awake) {
      wakeLock ??= (await navigator.wakeLock?.request('screen')) ?? null;
    } else {
      await wakeLock?.release();
      wakeLock = null;
    }
  } catch {
    // Not supported or not allowed right now (e.g. the tab is hidden); harmless.
  }
}
let wakeLock: WakeLockSentinel | null = null;
